/* eslint-disable @typescript-eslint/no-explicit-any */
import { cascadeEval } from 'utils/conditions';

type RefPath<E> = ((item: E) => string | number) | keyof E | 'id';

function resolveRef<E>(ref: E | string | number, refPath: RefPath<E>): string {
  return cascadeEval([
    { is: ['string', 'number'].includes(typeof ref), then: ref },
    { is: typeof refPath === 'function', then: () => (refPath as (e: E) => string)(ref as E) },
    { is: true, then: (ref as E)[refPath as keyof E] as string },
  ]) as string;
}

function extractRef<E>(target: E, path: RefPath<E>): string {
  const extracted = typeof path === 'function' ? path(target) : target[path as keyof E];

  return extracted as string;
}

export function listRemove<E>(list: E[], ref: string | E, path: RefPath<E> = 'id'): E[] {
  if (!ref || !list?.length) return list;

  const removeRef = resolveRef(ref, path);

  return list.filter((item) => extractRef(item, path) !== removeRef);
}

export function listGet<E>(
  list: E[],
  ref: string | number | E,
  path: RefPath<E> = 'id',
): E | undefined {
  if (!ref || !list?.length) return;

  const removeRef = resolveRef(ref, path);

  return list.find((item) => extractRef(item, path) === removeRef);
}

export function listGetIndex<E>(list: E[], ref: string | E, path: RefPath<E> = 'id'): number {
  if (!ref || !list?.length) return -1;

  const removeRef = resolveRef(ref, path);

  return list.findIndex((item) => extractRef(item, path) === removeRef);
}

export function listUpdate<E>(list: E[], ref: E, matchProp: keyof E | 'id' = 'id'): E[] {
  const prop = matchProp as keyof E;

  return list.map((item) => (item[prop] === ref[prop] ? ref : item));
}

export function listPartialUpdate<E>(
  list: E[],
  ref: Partial<E>,
  matchProp: keyof E | 'id' = 'id',
): E[] {
  const prop = matchProp as keyof E;

  return list.map((item) => (item[prop] === ref[prop] ? { ...item, ...ref } : item));
}

/**
 * A special wrapper to fill a whole the default `array.findIndex()` method has,
 * which returns `-1` if the index is not found
 *
 * With this you can leverage nullish checks like indexOrNull(list, x) ?? default
 * without the mental work of remembering the special -1 case
 */
export function findIndexOrNull<E>(
  list: E[],
  ref: string | E,
  path: RefPath<E> = 'id',
): number | null {
  if (!ref || !list?.length) return null;

  const removeRef = resolveRef(ref, path);

  const index = list.findIndex((item) => extractRef(item, path) === removeRef);

  return index >= 0 ? index : null;
}

export function findAndMap<T, Mapper extends (item: T, index: number) => any>(
  list: T[],
  mapper: Mapper,
): undefined | ReturnType<Mapper> {
  let index = 0;

  // eslint-disable-next-line no-restricted-syntax
  for (const item of list) {
    const result = mapper(item, index);

    if (result) return result;

    index += 1;
  }
}
